import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Slider,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
  Tab,
  Tabs
} from "@material-ui/core";
import {
  Add,
  PlayArrow,
  Pause,
  Stop,
  Delete,
  Visibility,
  Send,
  Announcement as CampaignIcon,
  Info,
  CheckCircle,
  ErrorOutline,
  HourglassEmpty,
  Refresh,
  TextFields,
  Image,
  Audiotrack,
  Videocam,
  ArrowUpward,
  ArrowDownward,
  CloudUpload,
  Block,
  PermPhoneMsg,
  TableChart,
  Wifi,
  CloudDone,
  TouchApp,
  Security,
  ExpandMore,
  ExpandLess
} from "@material-ui/icons";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import api from "../../services/api";
import ApiBroadcastWizard from "./ApiBroadcastWizard";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  root: { padding: theme.spacing(3), maxWidth: 1200, margin: "0 auto" },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    marginBottom: theme.spacing(3), flexWrap: "wrap", gap: theme.spacing(1)
  },
  infoBox: {
    background: "rgba(33,150,243,0.08)", border: "1px solid rgba(33,150,243,0.3)",
    borderRadius: theme.shape.borderRadius, padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2), display: "flex", gap: theme.spacing(1), alignItems: "flex-start"
  },
  warnBox: {
    background: "rgba(255,152,0,0.08)", border: "1px solid rgba(255,152,0,0.4)",
    borderRadius: theme.shape.borderRadius, padding: theme.spacing(1.5), marginTop: theme.spacing(1)
  },
  campaignCard: { marginBottom: theme.spacing(1.5), border: `1px solid ${theme.palette.divider}` },
  progressRow: { display: "flex", alignItems: "center", gap: theme.spacing(1), marginTop: theme.spacing(0.5) },
  progressBar: { flex: 1, height: 8, borderRadius: 4 },
  emptyState: { textAlign: "center", padding: theme.spacing(6), color: theme.palette.text.secondary },
  dialogSection: { marginBottom: theme.spacing(2) },
  sectionLabel: {
    fontWeight: 600, marginBottom: theme.spacing(1),
    color: theme.palette.primary.main, fontSize: "0.875rem"
  },
  msgItem: {
    border: `1px solid ${theme.palette.divider}`, borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5), marginBottom: theme.spacing(1), background: theme.palette.background.paper
  },
  msgItemHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: theme.spacing(1) },
  addBtns: { display: "flex", gap: theme.spacing(1), flexWrap: "wrap", marginTop: theme.spacing(1) },
  uploadArea: {
    border: `2px dashed ${theme.palette.divider}`, borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2), textAlign: "center", cursor: "pointer", marginBottom: theme.spacing(1),
    "&:hover": { borderColor: theme.palette.primary.main, background: "rgba(33,150,243,0.04)" },
    transition: "all 0.2s"
  },
  uploadedFile: {
    display: "flex", alignItems: "center", gap: theme.spacing(1),
    padding: theme.spacing(1), background: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius, marginBottom: theme.spacing(1)
  },
  execTable: {
    width: "100%", borderCollapse: "collapse",
    "& th, & td": { padding: theme.spacing(1), textAlign: "left", borderBottom: `1px solid ${theme.palette.divider}`, fontSize: "0.85rem" },
    "& th": { fontWeight: 600, background: theme.palette.action.hover, color: theme.palette.text.secondary }
  }
}));

const STATUS_LABELS = { draft: "Rascunho", running: "Enviando...", paused: "Pausado", completed: "Concluído", cancelled: "Cancelado" };
const STATUS_COLORS = { draft: "default", running: "primary", paused: "default", completed: "primary", cancelled: "secondary" };
const MENU_PROPS = { disablePortal: true };
const MSG_COLORS = { text: "#1976d2", image: "#388e3c", audio: "#f57c00", video: "#7b1fa2", contato: "#00897b" };
const MSG_LABELS = { text: "Texto", image: "Imagem", audio: "Áudio", video: "Vídeo", contato: "Contato + Botão" };
const MSG_ICONS = {
  text: <TextFields fontSize="small" />,
  image: <Image fontSize="small" />,
  audio: <Audiotrack fontSize="small" />,
  video: <Videocam fontSize="small" />,
  contato: <PermPhoneMsg fontSize="small" />
};
const ACCEPT_MAP = { image: "image/*", audio: "audio/*", video: "video/*" };

const buildExecutionMetrics = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  return {
    total: list.length,
    sent: list.filter(item => item.status === "sent").length,
    delivered: list.filter(item => Boolean(item.deliveredAt || item.readAt) || ["delivered", "read"].includes(String(item.metaStatus || "").toLowerCase())).length,
    read: list.filter(item => Boolean(item.readAt) || String(item.metaStatus || "").toLowerCase() === "read").length,
    clicked: list.filter(item => Boolean(item.buttonClickedAt)).length,
    failed: list.filter(item => item.status === "error" || String(item.metaStatus || "").toLowerCase() === "failed").length,
  };
};

const formatRecipientPhone = rawValue => {
  const digits = String(rawValue || "").replace(/\D/g, "");
  if (!digits) return "";
  const national = digits.startsWith("55") ? digits.slice(2) : digits;
  if (national.length === 11) {
    return `+55 (${national.slice(0, 2)}) ${national.slice(2, 7)}-${national.slice(7)}`;
  }
  if (national.length === 10) {
    return `+55 (${national.slice(0, 2)}) ${national.slice(2, 6)}-${national.slice(6)}`;
  }
  return digits.startsWith("55") ? `+${digits}` : digits;
};

const renderExecutionRecipient = execution => {
  const name = execution.contact?.name || execution.contactName || "";
  const phone = formatRecipientPhone(execution.contact?.number || execution.phoneNumber);
  if (!name && !phone) return "—";
  return (
    <Box>
      <Typography variant="body2">{name || phone}</Typography>
      {name && phone && <Typography variant="caption" color="textSecondary">{phone}</Typography>}
    </Box>
  );
};

const mkMsg = (type) => ({
  id: Date.now() + Math.random(), type, content: "", url: "", filename: "", uploading: false,
  vcardName: "", vcardPhone: "", vcardBody: "", vcardFooter: "",
  vcardMode: "single",
  vcardButtons: [{ label: "Falar agora", message: "" }]
});

const defaultForm = {
  name: "", type: "single",
  messages: [{ id: 1, type: "text", content: "", url: "", filename: "", uploading: false, vcardName: "", vcardPhone: "", vcardBody: "", vcardFooter: "", vcardMode: "single", vcardButtons: [{ label: "Falar agora", message: "" }] }],
  targetType: "all_contacts",
  targetValue: "",
  targetExceptionType: "tag",
  delaySeconds: 25,
  whatsappId: ""
};

// ─── File Upload Area ─────────────────────────────────────────────────────────
function FileUploadArea({ msgId, msgType, url, filename, uploading, onUpload, onRemove }) {
  const classes = useStyles();
  const inputRef = useRef();

  const accept = ACCEPT_MAP[msgType] || "*/*";

  const handleFile = async (file) => {
    if (!file) return;
    onUpload(msgId, null, null, true); // set uploading=true
    try {
      const fd = new FormData();
      fd.append("typeArch", "broadcast");
      fd.append("file", file);
      const res = await api.post("/broadcast/media-upload", fd);
      onUpload(msgId, res.data.url, res.data.originalname, false);
    } catch (err) {
      toast.error("Erro ao enviar arquivo: " + (err?.response?.data?.error || err.message));
      onUpload(msgId, null, null, false);
    }
  };

  if (url) {
    return (
      <Box className={classes.uploadedFile}>
        {MSG_ICONS[msgType]}
        <Typography variant="caption" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {filename || url}
        </Typography>
        <IconButton size="small" onClick={() => onRemove(msgId)} title="Remover arquivo">
          <Delete style={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      className={classes.uploadArea}
      onClick={() => !uploading && inputRef.current?.click()}
    >
      <input ref={inputRef} type="file" accept={accept} style={{ display: "none" }}
        onChange={e => handleFile(e.target.files[0])} />
      {uploading ? (
        <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <CircularProgress size={18} />
          <Typography variant="caption">Enviando arquivo...</Typography>
        </Box>
      ) : (
        <Box>
          <CloudUpload style={{ fontSize: 28, color: "#aaa", marginBottom: 4 }} />
          <Typography variant="body2" color="textSecondary">
            Clique para selecionar o {MSG_LABELS[msgType].toLowerCase()}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {msgType === "image" ? "JPG, PNG, GIF, WEBP" : msgType === "audio" ? "MP3, OGG, M4A" : "MP4"}
          </Typography>
        </Box>
      )}

    </Box>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Disparos() {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const canDeleteCampaign = Boolean(user && (
    user.profile === "admin" || user.profile === "super" || user.super
  ));
  const listFileInputRef = useRef(null);

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [kanbanLanes, setKanbanLanes] = useState([]);
  const [broadcastLists, setBroadcastLists] = useState([]);
  const [whatsappConnections, setWhatsappConnections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [tipExpanded, setTipExpanded] = useState(false);

  const [activeTab, setActiveTab] = useState(0);
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [listName, setListName] = useState("");
  const [listFile, setListFile] = useState(null);
  const [listSaving, setListSaving] = useState(false);

  const [newCampaignOpen, setNewCampaignOpen] = useState(false);
  const [apiCampaignOpen, setApiCampaignOpen] = useState(false);
  const [apiForm, setApiForm] = useState({ name: '', whatsappId: '', templateId: '', targetType: 'all_contacts', targetValue: '', targetListIds: '[]', delaySeconds: 25 });
  const [apiConnections, setApiConnections] = useState([]);
  const [approvedTemplates, setApprovedTemplates] = useState([]);
  const [apiSaving, setApiSaving] = useState(false);
  const [executionsOpen, setExecutionsOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [executions, setExecutions] = useState([]);
  const executionMetrics = buildExecutionMetrics(executions);

  const [form, setForm] = useState(defaultForm);

  // ─── Listas ───
  const handleCreateList = async () => {
    if (!listName.trim()) return toast.warn("Informe o nome da lista");
    if (!listFile) return toast.warn("Selecione um arquivo Excel ou CSV");
    setListSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", listName.trim());
      fd.append("typeArch", "broadcast");
      fd.append("file", listFile);
      await api.post("/broadcast/lists", fd);
      toast.success("Lista importada com sucesso!");
      setListDialogOpen(false);
      setListName("");
      setListFile(null);
      const res = await api.get("/broadcast/lists");
      setBroadcastLists(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.error) || "Erro ao importar lista";
      toast.error(msg);
    }
    setListSaving(false);
  };

  const handleDeleteList = async (id) => {
    if (!window.confirm("Excluir esta lista?")) return;
    try {
      await api.delete("/broadcast/lists/" + id);
      setBroadcastLists(prev => prev.filter(l => l.id !== id));
      toast.success("Lista removida");
    } catch { toast.error("Erro ao remover lista"); }
  };

  // ─── Fetch ──────────────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const cRes = await api.get("/broadcast/campaigns");
      setCampaigns(Array.isArray(cRes.data) ? cRes.data : []);
    } catch (err) {
      toast.error("Erro ao carregar disparos");
    }
    setLoading(false);
  }, []);

  const fetchSupport = useCallback(async () => {
    try {
      const res = await api.get("/tags/list");
      setTags(Array.isArray(res.data) ? res.data : []);
    } catch { setTags([]); }
    try {
      const res = await api.get("/tag/kanban");
      const lista = res.data?.lista || res.data || [];
      setKanbanLanes(Array.isArray(lista) ? lista : []);
    } catch { setKanbanLanes([]); }
    try {
      const res = await api.get("/whatsapp");
      const wpps = Array.isArray(res.data) ? res.data : (res.data?.whatsapps || []);
      setWhatsappConnections(wpps.filter(w => w.status === "CONNECTED" && (!w.channel || w.channel === "whatsapp")));
      setApiConnections(wpps.filter(w => w.status === "CONNECTED" && w.channel === "whatsapp_oficial"));
    } catch { setWhatsappConnections([]); }
    try {
      const res = await api.get("/broadcast/lists");
      setBroadcastLists(Array.isArray(res.data) ? res.data : []);
    } catch { setBroadcastLists([]); }
  }, []);

  const fetchTemplatesForApi = async (whatsappId) => {
    if (!whatsappId) { setApprovedTemplates([]); return; }
    try {
      const res = await api.get('/broadcast/templates', { params: { whatsappId } });
      setApprovedTemplates(Array.isArray(res.data) ? res.data : []);
    } catch { setApprovedTemplates([]); }
  };

  const openApiModal = () => {
    setApiForm({ name: '', whatsappId: '', templateId: '', targetType: 'all_contacts', targetValue: '', targetListIds: '[]', delaySeconds: 25 });
    setApprovedTemplates([]);
    setApiCampaignOpen(true);
  };

  const closeApiModal = () => setApiCampaignOpen(false);

  const setApiField = (field, value) => {
    setApiForm(previous => ({ ...previous, [field]: value }));
  };

  const handleApiTargetTypeChange = (targetType) => {
    setApiForm(previous => ({
      ...previous,
      targetType,
      targetValue: ''
    }));
  };

  const handleApiWppChange = (wppId) => {
    setApiForm(p => ({ ...p, whatsappId: wppId, templateId: '' }));
    fetchTemplatesForApi(wppId);
  };

  const handleSubmitApiCampaign = async () => {
    if (!apiForm.name.trim()) return toast.warning('Informe o nome da campanha');
    if (!apiForm.whatsappId) return toast.warning('Selecione a conexao API Oficial');
    if (!apiForm.templateId) return toast.warning('Selecione um template aprovado');
    if ((apiForm.targetType === 'tag' || apiForm.targetType === 'kanban') && !apiForm.targetValue)
      return toast.warning('Selecione o filtro');
    if (apiForm.targetType === 'list') {
      try { const ids = JSON.parse(apiForm.targetListIds || '[]'); if (!ids.length) return toast.warning('Selecione ao menos uma lista'); } catch {}
    }
    setApiSaving(true);
    try {
      await api.post('/broadcast/campaigns', {
        name: apiForm.name,
        type: 'single',
        channelType: 'official',
        templateId: Number(apiForm.templateId),
        message: '[]',
        mediaType: 'text', mediaUrl: '',
        targetType: apiForm.targetType,
        targetValue: apiForm.targetType === 'list' ? '' : apiForm.targetValue,
        targetListIds: apiForm.targetType === 'list' ? apiForm.targetListIds : null,
        delaySeconds: apiForm.delaySeconds,
        whatsappId: apiForm.whatsappId
      });
      toast.success('Campanha API criada!');
      closeApiModal();
      fetchData();
    } catch (err) {
      toast.error('Erro ao criar: ' + (err?.response?.data?.error || err.message));
    }
    setApiSaving(false);
  };

  useEffect(() => { fetchData(); fetchSupport(); }, [fetchData, fetchSupport]);

  useEffect(() => {
    if (!campaigns.some(c => c.status === "running")) return;
    const t = setInterval(fetchData, 5000);
    return () => clearInterval(t);
  }, [campaigns, fetchData]);

  // ─── Form helpers ────────────────────────────────────────────────────────────

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addMsg = (type) => setForm(f => ({ ...f, messages: [...f.messages, mkMsg(type)] }));

  const removeMsg = (id) => setForm(f => ({ ...f, messages: f.messages.filter(m => m.id !== id) }));

  const updateMsg = (id, key, val) =>
    setForm(f => ({ ...f, messages: f.messages.map(m => m.id === id ? { ...m, [key]: val } : m) }));

  const handleFileUpload = (id, url, filename, uploading) => {
    setForm(f => ({
      ...f,
      messages: f.messages.map(m => m.id === id
        ? { ...m, url: url || m.url, filename: filename || m.filename, uploading }
        : m
      )
    }));
  };

  const removeFileFromMsg = (id) => {
    setForm(f => ({ ...f, messages: f.messages.map(m => m.id === id ? { ...m, url: "", filename: "" } : m) }));
  };

  const moveMsg = (idx, dir) => setForm(f => {
    const msgs = [...f.messages];
    const ni = idx + dir;
    if (ni < 0 || ni >= msgs.length) return f;
    [msgs[idx], msgs[ni]] = [msgs[ni], msgs[idx]];
    return { ...f, messages: msgs };
  });

  const closeNewCampaign = () => { setNewCampaignOpen(false); setForm(defaultForm); };

  // ─── Campaign actions ────────────────────────────────────────────────────────

  const handleCreateCampaign = async () => {
    if (!form.name.trim()) return toast.warning("Informe um nome");
    if (form.type === "single") {
      if (!form.messages.length) return toast.warning("Adicione ao menos uma mensagem");
      for (const m of form.messages) {
        if (m.uploading) return toast.warning("Aguarde o upload terminar");
        if (m.type === "text" && !m.content.trim()) return toast.warning("Preencha o texto");
        if (m.type === "contato" && !m.vcardPhone.trim()) return toast.warning("Informe o número do contato");
        if (m.type === "contato" && !m.vcardName.trim()) return toast.warning("Informe o nome do contato");
        if (m.type !== "text" && m.type !== "contato" && !m.url.trim()) return toast.warning(`Faça upload do ${MSG_LABELS[m.type]}`);
      }
    }
    if ((form.targetType === "tag" || form.targetType === "kanban") && !form.targetValue)
      return toast.warning("Selecione o filtro");
    if (form.targetType === "exception" && !form.targetValue)
      return toast.warning("Selecione quem quer excluir");
    if (form.targetType === "list") {
      try {
        const ids = JSON.parse(form.targetValue || "[]");
        if (!ids.length) return toast.warning("Selecione ao menos uma lista");
      } catch { return toast.warning("Selecione ao menos uma lista"); }
    }

    setSaving(true);
    try {
      const cleanMsgs = form.messages.map(({ id, uploading, ...rest }) => rest);
      await api.post("/broadcast/campaigns", {
        name: form.name, type: form.type,
        message: JSON.stringify(cleanMsgs),
        mediaType: "text", mediaUrl: "",
        targetType: form.targetType, targetValue: form.targetValue,
        targetExceptionType: form.targetExceptionType,
        targetListIds: form.targetType === "list" ? form.targetValue : null,
        delaySeconds: form.delaySeconds,
        whatsappId: form.whatsappId || null
      });
      toast.success("Campanha criada!");
      closeNewCampaign();
      fetchData();
    } catch (err) {
      toast.error("Erro ao criar: " + (err?.response?.data?.error || err.message));
    }
    setSaving(false);
  };

  const act = (method, id) => async (campaign) => {
    try { await api.post(`/broadcast/campaigns/${campaign.id}/${method}`); fetchData(); }
    catch (err) { toast.error(err?.response?.data?.error || "Erro"); }
  };

  const handleStart = async campaign => {
    try {
      const { data: preview } = await api.get(`/broadcast/campaigns/${campaign.id}/audience-preview`);
      if (preview?.exceedsHardLimit) {
        return toast.error(`O público excede o limite de ${preview.hardLimit} contatos`);
      }
      if (preview?.confirmationRequired) {
        const confirmed = window.confirm(`Este disparo alcançará ${preview.eligibleCount} contatos. Deseja continuar?`);
        if (!confirmed) return;
      }
      await api.post(`/broadcast/campaigns/${campaign.id}/start`, {
        confirmedRecipientCount: preview?.confirmationRequired ? preview.eligibleCount : undefined
      });
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Não foi possível iniciar o disparo");
    }
  };
  const handlePause = act("pause");
  const handleResume = act("resume");
  const handleCancel = async (campaign) => {
    if (!window.confirm(`Cancelar "${campaign.name}"?`)) return;
    try { await api.post(`/broadcast/campaigns/${campaign.id}/cancel`); fetchData(); } catch {}
  };
  const handleDelete = async (campaign) => {
    if (!window.confirm(`Excluir "${campaign.name}"?`)) return;
    try { await api.delete(`/broadcast/campaigns/${campaign.id}`); fetchData(); } catch {}
  };
  const handleViewExec = async (campaign) => {
    setSelectedCampaign(campaign); setExecutions([]); setExecutionsOpen(true);
    try {
      const res = await api.get(`/broadcast/campaigns/${campaign.id}/executions`);
      setExecutions(Array.isArray(res.data) ? res.data : []);
    } catch {}
  };

  const getProgress = (c) => (!c.totalCount ? 0 : Math.round(((c.sentCount + c.errorCount) / c.totalCount) * 100));
  const parseMsgs = (campaign) => { try { return JSON.parse(campaign.message || "[]"); } catch { return []; } };
  const formatAuditDate = value => value
    ? new Date(value).toLocaleString("pt-BR")
    : "Data nao registrada";

  const targetLabel = (c) => {
    if (c.targetType === "all_contacts") return "Todos os contatos cadastrados";
    if (c.targetType === "all") return "Todos com conversa (legado)";
    if (c.targetType === "exception") return `Todos exceto ${c.targetExceptionType === "tag" ? "tag" : "kanban"}: ${c.targetValue}`;
    if (c.targetType === "tag") return `Tag: ${c.targetValue}`;
    if (c.targetType === "kanban") return `Kanban: ${c.targetValue}`;
    if (c.targetType === "list") {
      try {
        const ids = JSON.parse(c.targetValue || "[]");
        const names = broadcastLists.filter(l => ids.includes(l.id)).map(l => l.name);
        return names.length ? `Lista: ${names.join(", ")}` : "Lista de transmissão";
      } catch { return "Lista de transmissão"; }
    }
    return c.targetType;
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Box style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Send color="primary" style={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" style={{ fontWeight: 700 }}>Disparos em Massa</Typography>
            <Typography variant="body2" color="textSecondary">Envie mensagens para grupos de contatos</Typography>
          </Box>
        </Box>
        <Box style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Box style={{ display: "flex", gap: 8 }}>
            <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => setNewCampaignOpen(true)}>Disparo com WhatsApp QR Code</Button>
            <Button variant="outlined" color="primary" startIcon={<CloudDone />} onClick={openApiModal}
              style={{ borderColor: "#1976d2", color: "#1976d2" }}>
              Disparo com WhatsApp API
            </Button>
          </Box>
          <IconButton onClick={fetchData}><Refresh /></IconButton>
        </Box>
      </Box>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} indicatorColor="primary" textColor="primary" style={{ marginBottom: 16, borderBottom: "1px solid #e0e0e0" }}>
        <Tab label="Campanhas" style={{ minHeight: 48, textTransform: "none", fontWeight: 600 }} />
        <Tab label="Listas" style={{ minHeight: 48, textTransform: "none", fontWeight: 600 }} />
      </Tabs>

      {activeTab === 0 && (<Box>{/* Aviso: apenas WhatsApp */}
      <Box style={{
        background: "rgba(255,152,0,0.07)", border: "1px solid rgba(255,152,0,0.35)",
        borderRadius: 8, padding: "10px 14px", marginBottom: 12,
        display: "flex", alignItems: "flex-start", gap: 10
      }}>
        <span style={{ fontSize: 18, flexShrink: 0 }}>📢</span>
        <Box>
          <Typography variant="body2" style={{ fontWeight: 700, color: "#e65100" }}>
            Disparos funcionam apenas via WhatsApp
          </Typography>
          <Typography variant="body2" style={{ color: "#555", marginTop: 2, lineHeight: 1.6 }}>
            Instagram e Facebook <strong>não suportam envio em massa</strong> — apenas conversas individuais são permitidas nessas plataformas.
            {' '}O WhatsApp aceita <strong>texto, imagem, áudio, vídeo e arquivo</strong> nos disparos.
            {' '}Conexões Instagram/Facebook não aparecem na lista de conexões de saída.
          </Typography>
        </Box>
      </Box>

      {/* Dica: número descartável */}
      <Box style={{
        background: "rgba(33,150,243,0.06)", border: "1px solid rgba(33,150,243,0.25)",
        borderRadius: 8, marginBottom: 16, overflow: "hidden"
      }}>
        <Box
          onClick={() => setTipExpanded(p => !p)}
          style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", cursor: "pointer" }}
        >
          <Security style={{ color: "#1976d2", flexShrink: 0 }} />
          <Box style={{ flex: 1 }}>
            <Typography variant="body2" style={{ fontWeight: 600, color: "#1976d2" }}>
              Proteja seu número principal com disparos seguros
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Clique para ver como disparar em massa sem risco de banimento
            </Typography>
          </Box>
          {tipExpanded ? <ExpandLess style={{ color: "#1976d2" }} /> : <ExpandMore style={{ color: "#1976d2" }} />}
        </Box>
        {tipExpanded && (
          <Box style={{ padding: "0 14px 14px", borderTop: "1px solid rgba(33,150,243,0.15)" }}>
            <Typography variant="body2" style={{ marginTop: 10, lineHeight: 1.7 }}>
              <strong>Como funciona a estratégia de número seguro:</strong>
            </Typography>
            <Typography variant="body2" style={{ marginTop: 6, lineHeight: 1.8, color: "#444" }}>
              Ao fazer disparos em massa — especialmente para contatos frios ou grandes listas — existe risco de banimento do número usado. Para proteger seu número principal:
            </Typography>
            <Box component="ol" style={{ margin: "8px 0 8px 18px", padding: 0 }}>
              <Typography component="li" variant="body2" style={{ marginBottom: 4 }}>
                <strong>Conecte um número secundário</strong> (chip novo ou número descartável) em <em>Configurações → Conexões</em>
              </Typography>
              <Typography component="li" variant="body2" style={{ marginBottom: 4 }}>
                <strong>Crie o disparo</strong> normalmente e selecione o número secundário em <em>"Conexão de saída"</em>
              </Typography>
              <Typography component="li" variant="body2" style={{ marginBottom: 4 }}>
                <strong>Adicione um Cartão de Contato</strong> (tipo "Contato") com o nome e número do seu número oficial
              </Typography>
              <Typography component="li" variant="body2" style={{ marginBottom: 4 }}>
                <strong>Opcional:</strong> adicione um <em>Botão</em> com mensagem pré-definida — o lead clica e já cai no seu número principal com o assunto digitado
              </Typography>
            </Box>
            <Typography variant="body2" style={{ color: "#555", lineHeight: 1.7 }}>
              Resultado: o lead qualificado chega no seu número principal já sabendo o que quer. Se o número secundário for banido, você não perde nada — histórico e conexão principal ficam intactos.
            </Typography>
          </Box>
        )}
      </Box>


      {loading && <LinearProgress style={{ marginBottom: 16 }} />}

        {!loading && campaigns.length === 0 ? (
          <Box className={classes.emptyState}>
            <CampaignIcon style={{ fontSize: 64, opacity: 0.3, marginBottom: 8 }} />
            <Typography variant="h6">Nenhuma campanha criada</Typography>
            <Typography variant="body2">Clique em <strong>Nova Campanha</strong> para começar</Typography>
          </Box>
        ) : campaigns.map(campaign => {
          const msgs = parseMsgs(campaign);
          return (
            <Card key={campaign.id} className={classes.campaignCard} variant="outlined">
              <CardContent style={{ paddingBottom: 12 }}>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={12} sm={5}>
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{campaign.name}</Typography>
                    <Box style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                      <Chip label="Disparo Único" size="small" variant="outlined" />
                      {campaign.channelType === "official" && (
                        <Chip label="WhatsApp API" size="small" style={{ background: "#e3f2fd", color: "#1565c0", fontWeight: 600 }} />
                      )}
                      <Chip label={STATUS_LABELS[campaign.status] || campaign.status} color={STATUS_COLORS[campaign.status] || "default"} size="small" />
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {targetLabel(campaign)} · {campaign.delaySeconds}s intervalo
                      {msgs.length > 0 && ` · ${msgs.length} msg${msgs.length > 1 ? "s" : ""}`}
                    </Typography>
                    <Box style={{ marginTop: 6, display: "flex", flexDirection: "column" }}>
                      <Typography variant="caption" color="textSecondary">
                        Criado por: <strong>{campaign.createdByUserName || "Responsavel nao registrado"}</strong> - {formatAuditDate(campaign.createdAt)}
                      </Typography>
                      {campaign.startedAt && (
                        <Typography variant="caption" color="textSecondary">
                          Disparado por: <strong>{campaign.startedByUserName || "Responsavel nao registrado"}</strong> - {formatAuditDate(campaign.startedAt)}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {campaign.totalCount > 0 && (
                      <Box>
                        <Box className={classes.progressRow}>
                          <LinearProgress variant="determinate" value={getProgress(campaign)} className={classes.progressBar} />
                          <Typography variant="caption">{getProgress(campaign)}%</Typography>
                        </Box>
                        <Typography variant="caption" color="textSecondary">
                          {campaign.sentCount} enviados · {campaign.errorCount} erros · {campaign.totalCount} total
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Box style={{ display: "flex", justifyContent: "flex-end", gap: 4 }}>
                      {campaign.status === "draft" && <Tooltip title="Iniciar"><IconButton size="small" color="primary" onClick={() => handleStart(campaign)}><PlayArrow /></IconButton></Tooltip>}
                      {["completed", "cancelled"].includes(campaign.status) && <Tooltip title="Disparar novamente"><IconButton size="small" color="primary" onClick={() => handleStart(campaign)}><PlayArrow /></IconButton></Tooltip>}
                      {campaign.status === "running" && <Tooltip title="Pausar"><IconButton size="small" onClick={() => handlePause(campaign)}><Pause /></IconButton></Tooltip>}
                      {campaign.status === "paused" && <Tooltip title="Retomar"><IconButton size="small" color="primary" onClick={() => handleResume(campaign)}><PlayArrow /></IconButton></Tooltip>}
                      {["running", "paused"].includes(campaign.status) && <Tooltip title="Cancelar"><IconButton size="small" color="secondary" onClick={() => handleCancel(campaign)}><Stop /></IconButton></Tooltip>}
                      <Tooltip title="Ver execuções"><IconButton size="small" onClick={() => handleViewExec(campaign)}><Visibility /></IconButton></Tooltip>
                      {canDeleteCampaign && ["draft", "completed", "cancelled"].includes(campaign.status) && <Tooltip title="Excluir (somente administrador)"><IconButton size="small" onClick={() => handleDelete(campaign)}><Delete /></IconButton></Tooltip>}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          );
        })}

      {/* ═══════════════════════════════════════════════════════════
          DIALOG: Nova Campanha
      ═══════════════════════════════════════════════════════════ */}

      <ApiBroadcastWizard open={apiCampaignOpen} onClose={closeApiModal} onCreated={fetchData} />
      {/* API Campaign Dialog legado removido: a jornada agora e configurada integralmente no assistente acima. */}
      {false && <Dialog open={apiCampaignOpen} onClose={closeApiModal} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CloudDone style={{ color: '#1976d2' }} />
            <span>Nova Campanha WhatsApp API</span>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* Nome */}
          <Box style={{ marginBottom: 16 }}>
            <TextField label="Nome da campanha *" fullWidth variant="outlined" size="small"
              value={apiForm.name} onChange={e => setApiField('name', e.target.value)}
              placeholder="Ex: Promoção Maio via API" />
          </Box>
          {/* Conexao API */}
          <Box style={{ marginBottom: 16 }}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>Conexão WhatsApp API Oficial *</InputLabel>
              <Select value={apiForm.whatsappId || ''} onChange={e => handleApiWppChange(e.target.value)}
                label="Conexão WhatsApp API Oficial *" MenuProps={MENU_PROPS}>
                <MenuItem value=""><em>Selecione a conexão API</em></MenuItem>
                {apiConnections.map(w => (
                  <MenuItem key={w.id} value={w.id}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <CloudDone style={{ fontSize: 16, color: '#1976d2' }} />
                      {w.name || w.number || ('API ' + w.id)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {apiConnections.length === 0 && (
              <Typography variant="caption" style={{ color: '#e53935', marginTop: 4, display: 'block' }}>
                Nenhuma conexão API Oficial conectada. Configure em Configurações → Conexões.
              </Typography>
            )}
          </Box>
          {/* Template */}
          {apiForm.whatsappId && (
            <Box style={{ marginBottom: 16 }}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Template aprovado *</InputLabel>
                <Select value={apiForm.templateId || ''} onChange={e => setApiField('templateId', e.target.value)}
                  label="Template aprovado *" MenuProps={MENU_PROPS}>
                  <MenuItem value=""><em>Selecione o template</em></MenuItem>
                  {approvedTemplates.map(t => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.shortcode} {t.category ? '(' + t.category + ')' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {approvedTemplates.length === 0 && (
                <Typography variant="caption" style={{ color: '#e53935', marginTop: 4, display: 'block' }}>
                  Nenhum template aprovado encontrado para esta conexão. Crie templates em Templates Aprovados.
                </Typography>
              )}
            </Box>
          )}
          {/* Alvo */}
          <Box style={{ marginBottom: 12 }}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>Enviar para</InputLabel>
              <Select value={apiForm.targetType} onChange={e => handleApiTargetTypeChange(e.target.value)}
                label="Enviar para" MenuProps={MENU_PROPS}>
                <MenuItem value="all_contacts">Todos os contatos cadastrados</MenuItem>
                <MenuItem value="tag">Por etiqueta</MenuItem>
                <MenuItem value="kanban">Por aba do Kanban</MenuItem>
                <MenuItem value="list">Por lista de contatos</MenuItem>
                <MenuItem value="exception">Todos exceto...</MenuItem>
              </Select>
            </FormControl>
            {apiForm.targetType === "all_contacts" && (
              <Box className={classes.warnBox}>
                <Typography variant="caption">Inclui todos da aba Contatos, mesmo sem conversa anterior. Confirme o público antes de iniciar.</Typography>
              </Box>
            )}
          </Box>
          {(apiForm.targetType === 'tag') && (
            <Box style={{ marginBottom: 12 }}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Etiqueta</InputLabel>
                <Select value={apiForm.targetValue || ''} onChange={e => setApiField('targetValue', e.target.value)}
                  label="Etiqueta" MenuProps={MENU_PROPS}>
                  {tags.filter(t => !t.kanban).map(t => <MenuItem key={t.id} value={t.name}>{t.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          )}
          {(apiForm.targetType === 'kanban') && (
            <Box style={{ marginBottom: 12 }}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Aba do Kanban</InputLabel>
                <Select value={apiForm.targetValue || ''} onChange={e => setApiField('targetValue', e.target.value)}
                  label="Aba do Kanban" MenuProps={MENU_PROPS}>
                  {kanbanLanes.map(l => <MenuItem key={l.id} value={l.name}>{l.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          )}
          {apiForm.targetType === 'exception' && (
            <Box style={{ marginBottom: 12 }}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Excluir etiqueta</InputLabel>
                <Select value={apiForm.targetValue || ''} onChange={e => setApiField('targetValue', e.target.value)}
                  label="Excluir etiqueta" MenuProps={MENU_PROPS}>
                  {tags.filter(t => !t.kanban).map(t => <MenuItem key={t.id} value={t.name}>{t.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          )}
          {apiForm.targetType === 'list' && (
            <Box style={{ marginBottom: 12 }}>
              <Typography variant="caption" color="textSecondary" style={{ marginBottom: 6, display: 'block' }}>Selecione as listas:</Typography>
              {broadcastLists.map(lst => {
                const ids = (() => { try { return JSON.parse(apiForm.targetListIds || '[]'); } catch { return []; } })();
                const checked = ids.includes(lst.id);
                return (
                  <FormControlLabel key={lst.id} control={
                    <Checkbox checked={checked} color="primary" onChange={e => {
                      const next = e.target.checked ? [...ids, lst.id] : ids.filter(i => i !== lst.id);
                      setApiForm(p => ({ ...p, targetListIds: JSON.stringify(next) }));
                    }} />}
                    label={lst.name + ' (' + (lst.contacts?.length || 0) + ' contatos)'} />
                );
              })}
            </Box>
          )}
          {/* Delay */}
          <Box style={{ marginTop: 8 }}>
            <Typography variant="caption" color="textSecondary">Intervalo entre envios: {apiForm.delaySeconds}s</Typography>
            <Slider min={5} max={120} step={5} value={apiForm.delaySeconds}
              onChange={(_, v) => setApiForm(p => ({ ...p, delaySeconds: v }))}
              marks={[{value:5,label:'5s'},{value:60,label:'1min'},{value:120,label:'2min'}]} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApiModal} disabled={apiSaving}>Cancelar</Button>
          <Button onClick={handleSubmitApiCampaign} variant="contained" color="primary" disabled={apiSaving}>
            {apiSaving ? 'Criando...' : 'Criar Campanha API'}
          </Button>
        </DialogActions>
      </Dialog>}

      <Dialog open={newCampaignOpen} onClose={closeNewCampaign} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle>
          <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Send color="primary" /> Nova Campanha de Disparo
          </Box>
        </DialogTitle>
        <DialogContent dividers>

          {/* Nome + Conexão de saída */}
          <Box className={classes.dialogSection}>
            <TextField label="Nome da campanha *" fullWidth variant="outlined" size="small"
              value={form.name} onChange={e => setField("name", e.target.value)}
              placeholder="Ex: Promoção Abril, Lembrete Reunião..."
              style={{ marginBottom: 10 }} />
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>Conexão de saída (qual WhatsApp envia)</InputLabel>
              <Select
                value={form.whatsappId || ""}
                onChange={e => setField("whatsappId", e.target.value)}
                label="Conexão de saída (qual WhatsApp envia)"
                MenuProps={MENU_PROPS}
              >
                <MenuItem value=""><em>Automático (conexão QR padrão)</em></MenuItem>
                {whatsappConnections.map(w => (
                  <MenuItem key={w.id} value={w.id}>
                    <Box style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Wifi style={{ fontSize: 16, color: "#4caf50" }} />
                      {w.name || w.number || ("Conexão " + w.id)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>



          {/* ── Disparo Único: lista de mensagens ── */}
            <Box className={classes.dialogSection}>
              <Typography className={classes.sectionLabel}>
                Mensagens ({form.messages.length}) — enviadas em sequência para cada contato
              </Typography>

              {form.messages.map((msg, idx) => (
                <Box key={msg.id} className={classes.msgItem}>
                  <Box className={classes.msgItemHeader}>
                    <Box style={{ display: "flex", alignItems: "center", gap: 4, color: MSG_COLORS[msg.type], fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase" }}>
                      {MSG_ICONS[msg.type]}
                      <span style={{ marginLeft: 4 }}>{idx + 1}. {MSG_LABELS[msg.type]}</span>
                    </Box>
                    <Box style={{ display: "flex", gap: 2 }}>
                      <Tooltip title="Mover para cima"><span><IconButton size="small" onClick={() => moveMsg(idx, -1)} disabled={idx === 0}><ArrowUpward style={{ fontSize: 14 }} /></IconButton></span></Tooltip>
                      <Tooltip title="Mover para baixo"><span><IconButton size="small" onClick={() => moveMsg(idx, 1)} disabled={idx === form.messages.length - 1}><ArrowDownward style={{ fontSize: 14 }} /></IconButton></span></Tooltip>
                      <Tooltip title="Remover"><span><IconButton size="small" onClick={() => removeMsg(msg.id)} disabled={form.messages.length === 1}><Delete style={{ fontSize: 14 }} /></IconButton></span></Tooltip>
                    </Box>
                  </Box>

                  {msg.type === "text" ? (
                    <TextField label="Texto *" fullWidth multiline rows={3} variant="outlined" size="small"
                      value={msg.content} onChange={e => updateMsg(msg.id, "content", e.target.value)}
                      placeholder="Digite o texto que será enviado..." />
                  ) : msg.type === "contato" ? (
                    <Box style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <Box style={{ display: "flex", gap: 8 }}>
                        <TextField label="Identificar contato (interno)" size="small" variant="outlined" style={{ flex: 1 }}
                          value={msg.vcardName || ""}
                          onChange={e => updateMsg(msg.id, "vcardName", e.target.value)}
                          placeholder="Ex: Suporte Estratiza Hub" />
                        <TextField label="Número (DDI+DDD) *" size="small" variant="outlined" style={{ flex: 1 }}
                          value={msg.vcardPhone || ""}
                          onChange={e => updateMsg(msg.id, "vcardPhone", e.target.value.replace(/\D/g, ""))}
                          placeholder="5511999999999" inputProps={{ inputMode: "numeric" }} />
                      </Box>
                      <TextField label="Texto acima do botão" size="small" variant="outlined" fullWidth multiline rows={2}
                        value={msg.vcardBody || ""}
                        onChange={e => updateMsg(msg.id, "vcardBody", e.target.value)}
                        placeholder="Ex: Olá! Temos uma oferta especial para você 👇" />
                      <TextField label="Rodapé (opcional)" size="small" variant="outlined" fullWidth
                        value={msg.vcardFooter || ""}
                        onChange={e => updateMsg(msg.id, "vcardFooter", e.target.value)}
                        placeholder="Ex: Estratiza Hub" />
                      <FormControl size="small" variant="outlined" fullWidth>
                        <InputLabel>Modo</InputLabel>
                        <Select value={msg.vcardMode || "single"}
                          onChange={e => updateMsg(msg.id, "vcardMode", e.target.value)}
                          label="Modo" MenuProps={MENU_PROPS}>
                          <MenuItem value="single">Botão único</MenuItem>
                          <MenuItem value="list">Lista de opções (até 3)</MenuItem>
                        </Select>
                      </FormControl>
                      {(msg.vcardButtons || [{ label: "Falar agora", message: "" }]).slice(0, msg.vcardMode === "list" ? 3 : 1).map((btn, bi) => (
                        <Box key={bi} style={{ border: "1px solid #e0e0e0", borderRadius: 6, padding: 10 }}>
                          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 6 }}>
                            {msg.vcardMode === "list" ? `Opção ${bi + 1}` : "Botão"}
                          </Typography>
                          <Box style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <TextField label="Texto do botão *" size="small" variant="outlined" fullWidth
                              value={btn.label || ""}
                              onChange={e => { const b = [...(msg.vcardButtons || [])]; b[bi] = { ...b[bi], label: e.target.value }; updateMsg(msg.id, "vcardButtons", b); }}
                              placeholder="Ex: Falar agora" inputProps={{ maxLength: 20 }} helperText="Máx. 20 caracteres" />
                            <TextField label="Mensagem pré-definida (aparece digitada ao clicar) *" size="small" variant="outlined" fullWidth multiline rows={2}
                              value={btn.message || ""}
                              onChange={e => { const b = [...(msg.vcardButtons || [])]; b[bi] = { ...b[bi], message: e.target.value }; updateMsg(msg.id, "vcardButtons", b); }}
                              placeholder="Ex: Olá! Vi o anúncio e quero saber mais" />
                          </Box>
                        </Box>
                      ))}
                      {msg.vcardMode === "list" && (msg.vcardButtons || []).length < 3 && (
                        <Button size="small" variant="outlined" onClick={() => { const b = [...(msg.vcardButtons || []), { label: "", message: "" }]; updateMsg(msg.id, "vcardButtons", b.slice(0, 3)); }}>+ Adicionar opção</Button>
                      )}
                      <Box style={{ background: "rgba(0,137,123,0.06)", border: "1px solid rgba(0,137,123,0.25)", borderRadius: 6, padding: "8px 12px" }}>
                        <Typography variant="caption" style={{ color: "#00695c" }}>
                          <strong>Como chega:</strong> o destinatário vê apenas o texto acima + botão(ões) clicáveis. Nome e número não aparecem na mensagem recebida. Ao clicar, abre o WhatsApp com a mensagem já digitada.</Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box>
                      <FileUploadArea
                        msgId={msg.id} msgType={msg.type}
                        url={msg.url} filename={msg.filename} uploading={msg.uploading}
                        onUpload={handleFileUpload} onRemove={removeFileFromMsg}
                      />
                      {msg.type !== "audio" && (
                        <TextField label="Legenda (opcional)" fullWidth variant="outlined" size="small"
                          value={msg.content} onChange={e => updateMsg(msg.id, "content", e.target.value)}
                          placeholder="Legenda opcional..." />
                      )}
                    </Box>
                  )}
                </Box>
              ))}

              <Box className={classes.addBtns}>
                <Typography variant="caption" color="textSecondary" style={{ alignSelf: "center", marginRight: 4 }}>Adicionar:</Typography>
                {[
                  { type: "text", icon: <TextFields style={{ fontSize: 14 }} />, label: "Texto" },
                  { type: "image", icon: <Image style={{ fontSize: 14 }} />, label: "Imagem" },
                  { type: "audio", icon: <Audiotrack style={{ fontSize: 14 }} />, label: "Áudio" },
                  { type: "video", icon: <Videocam style={{ fontSize: 14 }} />, label: "Vídeo" },
                  { type: "contato", icon: <PermPhoneMsg style={{ fontSize: 14 }} />, label: "Contato + Botão" }
                ].map(opt => (
                  <Button key={opt.type} size="small" variant="outlined" startIcon={opt.icon}
                    onClick={() => addMsg(opt.type)}
                    style={{ borderColor: MSG_COLORS[opt.type], color: MSG_COLORS[opt.type] }}>
                    {opt.label}
                  </Button>
                ))}
              </Box>
            </Box>


          {/* ── Público-Alvo (4 opções) ── */}
          <Box className={classes.dialogSection}>
            <Typography className={classes.sectionLabel}>Público-Alvo</Typography>
            <FormControl fullWidth size="small" variant="outlined" style={{ marginBottom: 10 }}>
              <InputLabel>Quem receberá o disparo</InputLabel>
              <Select value={form.targetType}
                onChange={e => { setField("targetType", e.target.value); setField("targetValue", ""); }}
                label="Quem receberá o disparo" MenuProps={MENU_PROPS}>
                <MenuItem value="all_contacts">
                  <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Send style={{ fontSize: 16, color: "#1976d2" }} />
                    <Box><Typography variant="body2" style={{ fontWeight: 600 }}>Todos os contatos</Typography><Typography variant="caption" color="textSecondary">Todos da aba Contatos, mesmo sem conversa anterior</Typography></Box>
                  </Box>
                </MenuItem>
                <MenuItem value="exception">
                  <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Block style={{ fontSize: 16, color: "#f57c00" }} />
                    <Box><Typography variant="body2" style={{ fontWeight: 600 }}>Todos com exceção de...</Typography><Typography variant="caption" color="textSecondary">Todos exceto os que estão em uma tag ou lane específica</Typography></Box>
                  </Box>
                </MenuItem>
                <MenuItem value="tag">
                  <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Chip label="Tag" size="small" style={{ fontSize: 11, height: 18, background: "#e3f2fd" }} />
                    <Box><Typography variant="body2" style={{ fontWeight: 600 }}>Por tag específica</Typography><Typography variant="caption" color="textSecondary">Somente contatos com uma tag selecionada</Typography></Box>
                  </Box>
                </MenuItem>
                <MenuItem value="kanban">
                  <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Chip label="Kanban" size="small" style={{ fontSize: 11, height: 18, background: "#f3e5f5" }} />
                    <Box><Typography variant="body2" style={{ fontWeight: 600 }}>Por lane do Kanban</Typography><Typography variant="caption" color="textSecondary">Somente contatos em uma lane específica</Typography></Box>
                  </Box>
                </MenuItem>
                <MenuItem value="list">
                  <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Chip label="Lista" size="small" style={{ fontSize: 11, height: 18, background: "#e8f5e9" }} />
                    <Box><Typography variant="body2" style={{ fontWeight: 600 }}>Lista de transmissão</Typography><Typography variant="caption" color="textSecondary">Contatos de uma lista importada (CSV/Excel)</Typography></Box>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            {form.targetType === "all_contacts" && (
              <Box className={classes.warnBox} style={{ marginTop: 0, marginBottom: 10 }}>
                <Typography variant="caption">Inclui contatos sem ticket. Em QR Code, disparos para quem nunca conversou aumentam o risco de bloqueio do número; mantenha o intervalo entre envios.</Typography>
              </Box>
            )}

            {/* Exceção: escolher tipo + valor */}
            {form.targetType === "exception" && (
              <Box>
                <Box style={{ display: "flex", gap: 8 }}>
                  <Button size="small" variant={form.targetExceptionType === "tag" ? "contained" : "outlined"} color={form.targetExceptionType === "tag" ? "primary" : "default"}
                    onClick={() => { setField("targetExceptionType", "tag"); setField("targetValue", ""); }}>
                    Excluir por Tag
                  </Button>
                  <Button size="small" variant={form.targetExceptionType === "kanban" ? "contained" : "outlined"} color={form.targetExceptionType === "kanban" ? "primary" : "default"}
                    onClick={() => { setField("targetExceptionType", "kanban"); setField("targetValue", ""); }}>
                    Excluir por Kanban
                  </Button>
                </Box>
                <Box style={{ marginTop: 8 }}>
                  {form.targetExceptionType === "tag" && (
                    tags.length === 0
                      ? <Typography variant="caption" color="textSecondary">Nenhuma tag encontrada.</Typography>
                      : <FormControl fullWidth size="small" variant="outlined">
                          <InputLabel>Tag a excluir</InputLabel>
                          <Select value={form.targetValue} onChange={e => setField("targetValue", String(e.target.value))} label="Tag a excluir" MenuProps={MENU_PROPS}>
                            {tags.map(t => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
                          </Select>
                        </FormControl>
                  )}
                  {form.targetExceptionType === "kanban" && (
                    kanbanLanes.length === 0
                      ? <Typography variant="caption" color="textSecondary">Nenhuma lane Kanban encontrada.</Typography>
                      : <FormControl fullWidth size="small" variant="outlined">
                          <InputLabel>Lane a excluir</InputLabel>
                          <Select value={form.targetValue} onChange={e => setField("targetValue", e.target.value)} label="Lane a excluir" MenuProps={MENU_PROPS}>
                            {kanbanLanes.map(l => <MenuItem key={l.id || l.name} value={l.name}>{l.name}</MenuItem>)}
                          </Select>
                        </FormControl>
                  )}
                </Box>
                {form.targetValue && (
                  <Box style={{ marginTop: 8, padding: "6px 10px", background: "rgba(255,152,0,0.1)", borderRadius: 4 }}>
                    <Typography variant="caption" style={{ color: "#e65100" }}>
                      ⚠️ Todos os contatos receberão o disparo, <strong>exceto</strong> os que estão em {form.targetExceptionType === "tag" ? "tag" : "lane"}: <strong>{tags.find(t => String(t.id) === form.targetValue)?.name || kanbanLanes.find(l => l.name === form.targetValue)?.name || form.targetValue}</strong>
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Tag */}
            {form.targetType === "tag" && (
              tags.length === 0
                ? <Typography variant="caption" color="textSecondary">Nenhuma tag encontrada.</Typography>
                : <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel>Tag</InputLabel>
                    <Select value={form.targetValue} onChange={e => setField("targetValue", String(e.target.value))} label="Tag" MenuProps={MENU_PROPS}>
                      {tags.map(t => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
                    </Select>
                  </FormControl>
            )}

            {/* Kanban */}
            {form.targetType === "kanban" && (
              kanbanLanes.length === 0
                ? <Typography variant="caption" color="textSecondary">Nenhuma lane Kanban.</Typography>
                : <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel>Lane Kanban</InputLabel>
                    <Select value={form.targetValue} onChange={e => setField("targetValue", e.target.value)} label="Lane Kanban" MenuProps={MENU_PROPS}>
                      {kanbanLanes.map(l => <MenuItem key={l.id || l.name} value={l.name}>{l.name}</MenuItem>)}
                    </Select>
                  </FormControl>
            )}

            {/* Lista de transmissão */}
            {form.targetType === "list" && (
              broadcastLists.length === 0
                ? <Box>
                    <Typography variant="caption" color="textSecondary">Nenhuma lista encontrada. Crie uma lista primeiro.</Typography>
                  </Box>
                : <Box>
                    <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 6 }}>
                      Selecione uma ou mais listas de transmissão:
                    </Typography>
                    {broadcastLists.map(l => {
                      const selectedIds = (() => { try { return JSON.parse(form.targetValue || "[]"); } catch { return []; } })();
                      const isSelected = selectedIds.includes(l.id);
                      const contactCount = l.contacts ? l.contacts.length : 0;
                      return (
                        <Box key={l.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, padding: "6px 10px", border: `1px solid ${isSelected ? "#4caf50" : "#e0e0e0"}`, borderRadius: 6, cursor: "pointer", background: isSelected ? "rgba(76,175,80,0.07)" : "#fff" }}
                          onClick={() => {
                            const cur = (() => { try { return JSON.parse(form.targetValue || "[]"); } catch { return []; } })();
                            const next = isSelected ? cur.filter(id => id !== l.id) : [...cur, l.id];
                            setField("targetValue", JSON.stringify(next));
                          }}>
                          <Checkbox checked={isSelected} size="small" style={{ padding: 0, color: isSelected ? "#4caf50" : undefined }} />
                          <Box style={{ flex: 1 }}>
                            <Typography variant="body2" style={{ fontWeight: 600 }}>{l.name}</Typography>
                            <Typography variant="caption" color="textSecondary">{contactCount} contato{contactCount !== 1 ? "s" : ""}</Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
            )}
          </Box>

          {/* Intervalo */}
          <Box className={classes.dialogSection}>
            <Typography className={classes.sectionLabel}>Intervalo entre Contatos</Typography>
            <Box style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <Slider value={form.delaySeconds} onChange={(_, v) => setField("delaySeconds", v)}
                min={1} max={300} step={1} style={{ flex: 1 }} color="primary"
                marks={[{ value: 15, label: "15s" }, { value: 60, label: "1min" }, { value: 300, label: "5min" }]} />
              <TextField value={form.delaySeconds}
                onChange={e => setField("delaySeconds", Math.max(1, Math.min(300, Number(e.target.value) || 1)))}
                type="number" variant="outlined" size="small" style={{ width: 80 }}
                InputProps={{ endAdornment: <InputAdornment position="end">s</InputAdornment> }} />
            </Box>
            <Box className={classes.warnBox}>
              <Typography variant="caption">
                <strong>Recomendado: 15s ou mais</strong> entre contatos. Intervalo muito curto aumenta risco de bloqueio.
                {form.delaySeconds < 10 && <strong style={{ color: "#d32f2f" }}> ⚠️ Risco alto de bloqueio!</strong>}
              </Typography>
            </Box>
          </Box>

        </DialogContent>
        <DialogActions>
          <Button onClick={closeNewCampaign} disabled={saving}>Cancelar</Button>
          <Button variant="outlined" onClick={() => setPreviewOpen(true)} disabled={saving} style={{ marginRight: 8 }}>
            Pré-visualizar
          </Button>
          <Button variant="contained" color="primary" onClick={handleCreateCampaign} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : "Criar Campanha"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════
          DIALOG: Execuções
      ═══════════════════════════════════════════════════════════ */}
      </Box>)}

      {activeTab === 1 && (
        <Box>
          <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>Listas de Transmissao</Typography>
            <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => { setListName(""); setListFile(null); setListDialogOpen(true); }}>Nova Lista</Button>
          </Box>
          {broadcastLists.length === 0 ? (
            <Box style={{ textAlign: "center", padding: "40px 20px", background: "#fafafa", borderRadius: 8, border: "1px solid #e0e0e0" }}>
              <TableChart style={{ fontSize: 48, opacity: 0.3, display: "block", margin: "0 auto 8px" }} />
              <Typography variant="h6" color="textSecondary">Nenhuma lista criada</Typography>
              <Typography variant="body2" color="textSecondary">Clique em Nova Lista e suba um arquivo Excel (.xlsx) ou CSV</Typography>
            </Box>
          ) : broadcastLists.map(l => (
            <Card key={l.id} variant="outlined" style={{ marginBottom: 10 }}>
              <CardContent style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <TableChart color="primary" style={{ flexShrink: 0 }} />
                <Box style={{ flex: 1 }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{l.name}</Typography>
                  <Typography variant="caption" color="textSecondary">{l.contacts ? l.contacts.length : 0} contato(s) importado(s)</Typography>
                </Box>
                <IconButton size="small" onClick={() => handleDeleteList(l.id)} title="Excluir"><Delete style={{ color: "#e53935" }} /></IconButton>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Dialog open={listDialogOpen} onClose={() => setListDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova Lista de Transmissao</DialogTitle>
        <DialogContent style={{ paddingTop: 16 }}>
          <TextField label="Nome da lista" value={listName} onChange={e => setListName(e.target.value)} fullWidth variant="outlined" size="small" style={{ marginBottom: 20 }} />
          <Box style={{ border: "2px dashed #bbb", borderRadius: 8, padding: "24px 16px", textAlign: "center", background: "#fafafa", cursor: "pointer" }} onClick={() => listFileInputRef.current && listFileInputRef.current.click()}>
            <TableChart style={{ fontSize: 36, color: "#bbb", marginBottom: 8 }} />
            <Typography variant="body2" color="textSecondary">{listFile ? listFile.name : "Clique para selecionar arquivo Excel (.xlsx) ou CSV (.csv)"}</Typography>
            <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>Colunas: nome | telefone (aceita com/sem 55, DDD, hifen, parenteses)</Typography>
            <input ref={listFileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} onChange={e => setListFile((e.target.files && e.target.files[0]) || null)} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setListDialogOpen(false)} disabled={listSaving}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handleCreateList} disabled={listSaving}>
            {listSaving ? <CircularProgress size={20} /> : "Importar Lista"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={executionsOpen} onClose={() => setExecutionsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Execuções — {selectedCampaign?.name}</DialogTitle>
        <DialogContent dividers>
          {executions.length > 0 && (
            <Grid container spacing={1} style={{ marginBottom: 16 }}>
              {[
                ["Total", executionMetrics.total, <Send fontSize="small" />, "#455a64"],
                ["Enviados", executionMetrics.sent, <CheckCircle fontSize="small" />, "#00875A"],
                ["Entregues", executionMetrics.delivered, <CloudDone fontSize="small" />, "#00875A"],
                ["Lidos", executionMetrics.read, <Visibility fontSize="small" />, "#00875A"],
                ["Cliques em botão", executionMetrics.clicked, <TouchApp fontSize="small" />, "#6a1b9a"],
                ["Falhas", executionMetrics.failed, <ErrorOutline fontSize="small" />, "#c62828"],
              ].map(([label, value, icon, color]) => (
                <Grid item xs={6} sm={4} md={2} key={label}>
                  <Box style={{ border: "1px solid #e2e8e5", borderRadius: 10, padding: "10px 8px", textAlign: "center", background: "#fff" }}>
                    <Box style={{ color, display: "flex", justifyContent: "center", alignItems: "center", gap: 5 }}>{icon}<strong>{value}</strong></Box>
                    <Typography variant="caption" color="textSecondary">{label}</Typography>
                  </Box>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  Cliques contabilizam respostas em botões interativos recebidas pela API Oficial. Abertura de links externos não é informada pela Meta.
                </Typography>
              </Grid>
            </Grid>
          )}
          {executions.length === 0 ? (
            <Box style={{ textAlign: "center", padding: 32, color: "#999" }}>
              <HourglassEmpty style={{ fontSize: 48, marginBottom: 8, opacity: 0.4 }} />
              <Typography>Nenhuma execução registrada.</Typography>
            </Box>
          ) : (
            <table className={classes.execTable}>
              <thead><tr><th>Status</th><th>Contato</th><th>Ticket</th><th>Enviado em</th><th>Botão</th><th>Detalhe</th></tr></thead>
              <tbody>
                {executions.map(exec => (
                  <tr key={exec.id}>
                    <td>
                      <Box style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        {exec.status === "sent" && <CheckCircle style={{ fontSize: 16, color: "#4caf50" }} />}
                        {exec.status === "error" && <ErrorOutline style={{ fontSize: 16, color: "#f44336" }} />}
                        {exec.status === "pending" && <HourglassEmpty style={{ fontSize: 16, color: "#999" }} />}
                        {exec.status === "sent"
                          ? ({ sent: "Enviado", delivered: "Entregue", read: "Lido", failed: "Falhou" }[exec.metaStatus] || "Aceito pela Meta")
                          : ({ pending: "Pendente", processing: "Processando", error: "Erro", skipped: "Ignorado" }[exec.status] || exec.status)}
                      </Box>
                    </td>
                    <td>{renderExecutionRecipient(exec)}</td>
                    <td>{exec.ticketId ? `#${exec.ticketId}` : "—"}</td>
                    <td>{exec.sentAt ? new Date(exec.sentAt).toLocaleString("pt-BR") : "—"}</td>
                    <td>{exec.buttonClickedAt ? (exec.buttonPayload || "Clicado") : "—"}</td>
                    <td style={{ color: exec.errorMessage || exec.metaErrorMessage ? "#f44336" : "inherit", maxWidth: 240 }}>
                      {exec.errorMessage || exec.metaErrorMessage || (exec.metaStatusAt ? `Atualizado ${new Date(exec.metaStatusAt).toLocaleString("pt-BR")}` : "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExecutionsOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
      {/* Dialog: Pre-visualizacao de disparo */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle style={{ background: "#075e54", color: "#fff", padding: "12px 20px" }}>
          Pre-visualizacao -- como o contato vai receber
        </DialogTitle>
        <DialogContent dividers style={{ background: "#ece5dd", minHeight: 200, padding: "16px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
          {(form.messages || []).map((msg, idx) => (
            <Box key={msg.id || idx} style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", maxWidth: "88%" }}>
              {msg.type === "text" && (
                <Box style={{ background: "#fff", borderRadius: "0 12px 12px 12px", padding: "8px 12px", boxShadow: "0 1px 2px rgba(0,0,0,0.15)", whiteSpace: "pre-wrap", fontSize: "0.9rem" }}>
                  {msg.content || <span style={{ color: "#aaa", fontStyle: "italic" }}>Texto vazio</span>}
                </Box>
              )}
              {msg.type === "image" && (
                <Box style={{ background: "#fff", borderRadius: "0 12px 12px 12px", padding: 8, boxShadow: "0 1px 2px rgba(0,0,0,0.15)", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 24 }}>[Foto]</span>
                  <Typography variant="caption">{msg.filename || "Imagem"}{msg.content ? <span><br/>{msg.content}</span> : null}</Typography>
                </Box>
              )}
              {msg.type === "audio" && (
                <Box style={{ background: "#fff", borderRadius: "0 12px 12px 12px", padding: 8, boxShadow: "0 1px 2px rgba(0,0,0,0.15)", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 24 }}>[Audio]</span>
                  <Typography variant="caption">{msg.filename || "Audio"}</Typography>
                </Box>
              )}
              {msg.type === "video" && (
                <Box style={{ background: "#fff", borderRadius: "0 12px 12px 12px", padding: 8, boxShadow: "0 1px 2px rgba(0,0,0,0.15)", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 24 }}>[Video]</span>
                  <Typography variant="caption">{msg.filename || "Video"}{msg.content ? <span><br/>{msg.content}</span> : null}</Typography>
                </Box>
              )}
              {(msg.type === "contato" || msg.type === "vcard") && (
                <Box style={{ background: "#fff", borderRadius: "0 12px 12px 12px", padding: "10px 14px", boxShadow: "0 1px 2px rgba(0,0,0,0.15)", minWidth: 180 }}>
                  {msg.vcardBody && <Typography variant="body2" style={{ whiteSpace: "pre-wrap", marginBottom: 10, fontSize: "0.9rem" }}>{msg.vcardBody}</Typography>}
                  {(msg.vcardButtons || [{ label: "Falar agora" }]).slice(0, msg.vcardMode === "list" ? 3 : 1).map((btn, bi) => (
                    <Box key={bi} style={{ borderTop: "1px solid #e0e0e0", marginTop: 6, paddingTop: 6, display: "flex", justifyContent: "center" }}>
                      <Typography variant="body2" style={{ color: "#128c7e", fontWeight: 600, fontSize: "0.9rem" }}>
                        [Btn] {btn.label || "Falar agora"}
                      </Typography>
                    </Box>
                  ))}
                  {msg.vcardFooter && <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>{msg.vcardFooter}</Typography>}
                </Box>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Typography variant="caption" color="textSecondary" style={{ flex: 1, paddingLeft: 8 }}>Simulacao visual -- layout real depende do WhatsApp.</Typography>
          <Button onClick={() => setPreviewOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
